(function () {
  'use strict';

  if (window.HomewallPanelCalculatorV2Loaded) return;
  window.HomewallPanelCalculatorV2Loaded = true;

  const DEFAULT_PANEL_WIDTH_CM = 120;
  const DEFAULT_PANEL_LENGTH_CM = 280;
  const WASTE_FACTOR = 1.10;
  const ADD_TO_CART_TEXT = 'Toevoegen aan winkelwagen';
  const calculatorCartState = {adding: false, needsReview: false};

  const numberFormatter = new Intl.NumberFormat('nl-NL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  function parsePanelDimensionsCm(title) {
    // Read length and width from the same dimension pair, in centimeters.
    const match = String(title || '').match(
      /(?:^|[^\d.,+\-])([1-9]\d*)\s*[x×]\s*([1-9]\d*)(?![\d.,])/i
    );
    if (!match) return null;
    const lengthCm = Number(match[1]);
    const widthCm = Number(match[2]);
    return Number.isSafeInteger(lengthCm) && lengthCm > 0 &&
      Number.isSafeInteger(widthCm) && widthCm > 0
      ? {lengthCm: lengthCm, widthCm: widthCm} : null;
  }

  // Pack lane pieces globally. Adding kerf to every piece and once to the
  // stock capacity preserves the existing rule: the last piece may end flush.
  function packPanelLanes(lanes, panelLengthMm, requiredReserveLength, options) {
    const kerfMm = 3;
    const capacity = panelLengthMm + kerfMm;
    const limits = {exactLanes: 120, searchNodes: options ? 10000 : 100000,
      memoStates: options ? 2000 : 20000, searchMs: 100,
      patternLanes: 2000, patternSize: 20, patternAttempts: 8, patternMs: 20};
    requiredReserveLength = requiredReserveLength === undefined ? 0 : requiredReserveLength;
    if (!lanes.length || !Number.isFinite(requiredReserveLength) || requiredReserveLength < 0 ||
        lanes.some(function (lane) {
          return !Number.isSafeInteger(lane.lengthMm) || lane.lengthMm <= 0 || lane.lengthMm > panelLengthMm;
        })) return null;
    const ordered = lanes.slice().sort(function (a, b) {
      return b.lengthMm - a.lengthMm || a.wall - b.wall || a.lane - b.lane;
    });
    const sizes = ordered.map(function (lane) { return lane.lengthMm + kerfMm; });
    const totalSize = sizes.reduce(function (sum, size) { return sum + size; }, 0);
    if (!Number.isSafeInteger(capacity) || !Number.isSafeInteger(totalSize)) return null;
    let lowerBound = Math.ceil(totalSize / capacity);
    // At most floor(capacity / size) pieces of at least this size fit a panel.
    sizes.forEach(function (size, index) {
      lowerBound = Math.max(lowerBound, Math.ceil((index + 1) / Math.floor(capacity / size)));
    });

    const fullCount = sizes.filter(function (size) { return size === capacity; }).length;
    const partialCount = sizes.length - fullCount;
    const smallestSize = sizes[sizes.length - 1];
    // If at most three partial pieces fit, every triple needs two small pieces:
    // two larger pieces plus even the smallest piece would exceed capacity.
    if (partialCount > 0 && Math.floor(capacity / smallestSize) === 3) {
      const smallCount = sizes.filter(function (size) {
        return size < capacity && 2 * size + smallestSize <= capacity;
      }).length;
      lowerBound = Math.max(lowerBound,
        fullCount + Math.ceil((partialCount - Math.floor(smallCount / 2)) / 2));
    }

    // Deterministic first-fit decreasing in O(n log n), including large inputs.
    // The max tree finds the first physical panel that can hold a whole lane.
    let treeSize = 1;
    while (treeSize < lanes.length) treeSize *= 2;
    const tree = new Float64Array(treeSize * 2);
    let bestBins = [];
    sizes.forEach(function (size, index) {
      let panel;
      if (tree[1] < size) {
        panel = bestBins.length;
        bestBins.push({remaining: capacity, pieces: []});
      } else {
        let node = 1;
        while (node < treeSize) {
          node = tree[node * 2] >= size ? node * 2 : node * 2 + 1;
        }
        panel = node - treeSize;
      }
      const bin = bestBins[panel];
      bin.remaining -= size;
      bin.pieces.push(index);
      let node = treeSize + panel;
      tree[node] = bin.remaining;
      while (node > 1) {
        node = Math.floor(node / 2);
        tree[node] = Math.max(tree[node * 2], tree[node * 2 + 1]);
      }
    });

    // Also try spreading lengths over the proven lower bound. This cheaply
    // solves repeated mixed patterns (e.g. 100/80/80) at any input size. A
    // successful packing at the lower bound is a certificate of optimality.
    if (lowerBound < bestBins.length) {
      const balanced = Array.from({length: lowerBound}, function () {
        return {remaining: capacity, pieces: []};
      });
      tree.fill(0);
      for (let panel = 0; panel < lowerBound; panel += 1) tree[treeSize + panel] = capacity;
      for (let node = treeSize - 1; node > 0; node -= 1) {
        tree[node] = Math.max(tree[node * 2], tree[node * 2 + 1]);
      }
      let complete = true;
      for (let index = 0; index < sizes.length; index += 1) {
        if (tree[1] < sizes[index]) { complete = false; break; }
        let node = 1;
        while (node < treeSize) {
          node = tree[node * 2] >= tree[node * 2 + 1] ? node * 2 : node * 2 + 1;
        }
        const bin = balanced[node - treeSize];
        bin.remaining -= sizes[index];
        bin.pieces.push(index);
        tree[node] = bin.remaining;
        while (node > 1) {
          node = Math.floor(node / 2);
          tree[node] = Math.max(tree[node * 2], tree[node * 2 + 1]);
        }
      }
      if (complete) bestBins = balanced;
    }

    let nodes = 0;
    let fallbackReason = null;
    const startedAt = performance.now();
    const deadline = options ? options.deadline : startedAt + limits.searchMs;
    const failed = new Set();
    const bins = [];
    const suffix = new Array(sizes.length + 1).fill(0);
    for (let index = sizes.length - 1; index >= 0; index -= 1) {
      suffix[index] = suffix[index + 1] + sizes[index];
    }

    function search(index, freeSpace) {
      if (bestBins.length === lowerBound || fallbackReason || bins.length >= bestBins.length) return;
      nodes += 1;
      if (nodes > limits.searchNodes) {
        fallbackReason = 'node-limit';
        return;
      }
      if ((nodes === 1 || nodes % 128 === 0) && performance.now() >= deadline) {
        fallbackReason = 'time-limit';
        return;
      }
      if (index === sizes.length) {
        bestBins = bins.map(function (bin) {
          return {remaining: bin.remaining, pieces: bin.pieces.slice()};
        });
        return;
      }
      const unopened = bestBins.length - 1 - bins.length;
      if (unopened < 0 || suffix[index] > freeSpace + unopened * capacity) return;
      const key = index + ':' + bins.map(function (bin) { return bin.remaining; })
        .sort(function (a, b) { return a - b; }).join(',');
      if (failed.has(key)) return;
      const size = sizes[index];
      // Tightest fits first concentrate spare length into larger useful tails.
      // Equal capacities are symmetric, irrespective of wall names or indices.
      const candidates = bins.map(function (bin, panel) { return panel; })
        .filter(function (panel) { return bins[panel].remaining >= size; })
        .sort(function (a, b) { return bins[a].remaining - bins[b].remaining || a - b; });
      let previous = -1;
      for (const panel of candidates) {
        const bin = bins[panel];
        if (bin.remaining === previous) continue;
        previous = bin.remaining;
        bin.remaining -= size;
        bin.pieces.push(index);
        search(index + 1, freeSpace - size);
        bin.pieces.pop();
        bin.remaining += size;
        if (bestBins.length === lowerBound || fallbackReason) return;
      }
      if (bins.length + 1 < bestBins.length) {
        bins.push({remaining: capacity - size, pieces: [index]});
        search(index + 1, freeSpace + capacity - size);
        bins.pop();
      }
      if (!fallbackReason && failed.size < limits.memoStates) failed.add(key);
    }

    if (bestBins.length > lowerBound) {
      if (lanes.length > limits.exactLanes) fallbackReason = 'lane-limit';
      else search(0, 0);
    }
    // A certificate checks piece identity, exact integer kerf and stock capacity.
    // Invalid candidates can never replace the existing, validated packing.
    function validPacking(candidate) {
      const seen = new Uint8Array(sizes.length);
      let count = 0;
      for (const bin of candidate) {
        if (!bin.pieces.length) return false;
        let remaining = capacity;
        for (const index of bin.pieces) {
          if (!Number.isSafeInteger(index) || index < 0 || index >= sizes.length || seen[index]) return false;
          seen[index] = 1;
          count += 1;
          remaining -= sizes[index];
        }
        if (remaining < 0 || remaining !== bin.remaining) return false;
      }
      return count === sizes.length;
    }
    function finalPanelCount(candidate) {
      const remainingLength = candidate.reduce(function (sum, bin) {
        return sum + Math.max(0, bin.remaining - kerfMm);
      }, 0) / 1000;
      return candidate.length + Math.max(0, Math.ceil(
        (Math.max(0, requiredReserveLength - remainingLength) - 1e-9) / (panelLengthMm / 1000)));
    }
    if (!validPacking(bestBins)) return null;
    const validatedBaseline = bestBins;
    let patternAttempts = 0;
    let patternSearchNodes = 0;
    let patternRepeat = null;
    // Large repeated walls often need only a tiny exact pattern, repeated many
    // times. Optimize that pattern, then pack the remainder with the same engine.
    // Keep the existing solution if time, size or reserve requirements prevent an improvement.
    if (!options && fallbackReason && bestBins.length > lowerBound && partialCount <= limits.patternLanes) {
      const patternDeadline = Math.min(deadline, performance.now() + limits.patternMs);
      const grouped = new Map();
      const fullPieces = [];
      sizes.forEach(function (size, index) {
        if (size === capacity) fullPieces.push(index);
        else {
          if (!grouped.has(size)) grouped.set(size, []);
          grouped.get(size).push(index);
        }
      });
      const groups = Array.from(grouped.values());
      const groupForSize = new Map(groups.map(function (group, index) { return [sizes[group[0]], index]; }));
      const counts = groups.map(function (group) { return group.length; });
      const repeats = new Set();
      Array.from(new Set(counts)).sort(function (a, b) { return b - a; }).slice(0, 6).forEach(function (count) {
        [1, 2, 3].forEach(function (factor) {
          const repeat = Math.floor(count / factor);
          if (repeat >= 2) repeats.add(repeat);
        });
      });
      const repeatCounts = Array.from(repeats).sort(function (a, b) { return b - a; });
      // Prefer a complete repeated block before partial patterns with a large remainder.
      const commonRepeat = counts.reduce(function (a, b) {
        while (b) { const remainder = a % b; a = b; b = remainder; }
        return a;
      }, 0);
      if (commonRepeat >= 2) {
        const index = repeatCounts.indexOf(commonRepeat);
        if (index >= 0) repeatCounts.splice(index, 1);
        repeatCounts.unshift(commonRepeat);
      }
      const patternOptions = {deadline: patternDeadline};
      for (const repeat of repeatCounts) {
        if (bestBins.length === lowerBound || patternAttempts >= limits.patternAttempts ||
            performance.now() >= patternDeadline) break;
        const takes = counts.map(function (count) { return Math.floor(count / repeat); });
        const sample = [];
        groups.forEach(function (group, index) {
          group.slice(0, takes[index]).forEach(function (piece) { sample.push(ordered[piece]); });
        });
        if (sample.length < 2 || sample.length > limits.patternSize) continue;
        patternAttempts += 1;
        const pattern = packPanelLanes(sample, panelLengthMm, 0, patternOptions);
        if (!pattern) continue;
        patternSearchNodes += pattern.diagnostics.searchNodes;
        if (fullCount + repeat * pattern.bins.length >= bestBins.length) continue;
        const positions = counts.map(function () { return 0; });
        const candidate = fullPieces.map(function (index) { return {remaining: 0, pieces: [index]}; });
        for (let block = 0; block < repeat; block += 1) {
          pattern.bins.forEach(function (bin) {
            let remaining = capacity;
            const pieces = bin.lanes.map(function (lane) {
              const groupIndex = groupForSize.get(lane.lengthMm + kerfMm);
              const index = groups[groupIndex][positions[groupIndex]++];
              remaining -= sizes[index];
              return index;
            });
            candidate.push({remaining: remaining, pieces: pieces});
          });
        }
        const rest = [];
        groups.forEach(function (group, index) {
          group.slice(positions[index]).forEach(function (piece) { rest.push(ordered[piece]); });
        });
        if (rest.length) {
          const restPlan = packPanelLanes(rest, panelLengthMm, 0, patternOptions);
          if (!restPlan) continue;
          patternSearchNodes += restPlan.diagnostics.searchNodes;
          restPlan.bins.forEach(function (bin) {
            let remaining = capacity;
            const pieces = bin.lanes.map(function (lane) {
              const groupIndex = groupForSize.get(lane.lengthMm + kerfMm);
              const index = groups[groupIndex][positions[groupIndex]++];
              remaining -= sizes[index];
              return index;
            });
            candidate.push({remaining: remaining, pieces: pieces});
          });
        }
        if (candidate.length < bestBins.length && validPacking(candidate) &&
            finalPanelCount(candidate) <= finalPanelCount(bestBins)) {
          bestBins = candidate;
          patternRepeat = repeat;
        }
      }
    }
    if (!validPacking(bestBins)) {
      bestBins = validatedBaseline;
      fallbackReason = 'invalid-candidate';
    }
    const normalOptimal = bestBins.length === lowerBound || fallbackReason === null;
    const normalLowerBound = normalOptimal ? bestBins.length : lowerBound;
    // A packing proof and a reserve proof are distinct. End fragments shorter
    // than kerf can change reserve rounding; never infer a minimum from count alone.
    let finalLowerBound = Math.max(normalLowerBound,
      Math.ceil((totalSize + requiredReserveLength * 1000 - 1e-6) / capacity));
    if (panelLengthMm % 10 === 0 && ordered.every(function (lane) { return lane.lengthMm % 10 === 0; }) &&
        (partialCount === 0 || Math.floor(capacity / smallestSize) <= 3)) {
      // With integer-cm lengths and at most three pieces, a partial stock always
      // leaves enough for the final 3 mm cut. Full-length pieces need no cut.
      finalLowerBound = Math.max(finalLowerBound, Math.ceil(
        (totalSize - kerfMm * fullCount + requiredReserveLength * 1000 - 1e-6) / panelLengthMm));
    }
    const finalPanels = finalPanelCount(bestBins);
    const optimal = finalPanels === finalLowerBound;
    const finalFallback = optimal ? null : (fallbackReason || 'reserve-bound');
    return {
      bins: bestBins.map(function (bin) {
        return {remainingMm: Math.max(0, bin.remaining - kerfMm),
          lanes: bin.pieces.map(function (index) { return ordered[index]; })};
      }),
      diagnostics: {optimal: optimal, fallbackReason: finalFallback,
        normalOptimal: normalOptimal, normalLowerBound: normalLowerBound,
        finalLowerBound: finalLowerBound, finalPanels: finalPanels, certified: true,
        lanes: lanes.length, lowerBound: lowerBound, normalPanels: bestBins.length,
        searchNodes: nodes, memoStates: failed.size, patternAttempts: patternAttempts,
        patternSearchNodes: patternSearchNodes, patternRepeat: patternRepeat,
        searchMs: performance.now() - startedAt, limits: limits}
    };
  }

  function planWallsWithOffcuts(walls, wasteFactor, trace, panelHeight, panelWidth) {
    if (!walls.length || !Number.isFinite(wasteFactor) || wasteFactor < 1 ||
        !Number.isFinite(panelHeight) || panelHeight <= 0 ||
        !Number.isFinite(panelWidth) || panelWidth <= 0) return null;
    const panelLengthMm = Math.round(panelHeight * 1000);
    if (!Number.isSafeInteger(panelLengthMm) || panelLengthMm <= 0) return null;
    const entries = walls.map(function (wall, index) {
      return {index: index, width: wall.width, height: wall.height};
    });
    const lanes = [];
    let horizontalSeams = 0;
    let smallestAdditionalPieceMm = null;
    for (const wall of entries) {
      if (!Number.isFinite(wall.width) || !Number.isFinite(wall.height) ||
          wall.width <= 0 || wall.height <= 0) return null;
      const count = Math.ceil((wall.width - 1e-9) / panelWidth);
      const lengthMm = Math.round(wall.height * 1000);
      const segments = Math.ceil(lengthMm / panelLengthMm);
      // Preserve the 5,000-lane per-wall bound and cap all allocated pieces.
      if (!Number.isSafeInteger(count) || count < 1 || count > 5000 ||
          !Number.isSafeInteger(lengthMm) || lengthMm <= 0 ||
          !Number.isSafeInteger(segments) || segments < 1 ||
          lanes.length + count * segments > 50000) return null;
      horizontalSeams += count * (segments - 1);
      const additionalMm = lengthMm % panelLengthMm;
      if (segments > 1 && additionalMm > 0) {
        smallestAdditionalPieceMm = smallestAdditionalPieceMm === null
          ? additionalMm : Math.min(smallestAdditionalPieceMm, additionalMm);
      }
      for (let lane = 0; lane < count; lane += 1) {
        // Full product lengths plus one positive remainder cover the wall.
        // Kerf belongs to stock cuts, not to the joins between installed pieces.
        for (let segment = 0; segment < segments; segment += 1) {
          const targetYMm = segment * panelLengthMm;
          lanes.push({wall: wall.index, name: walls[wall.index].name, lane: lane,
            targetYMm: targetYMm, lengthMm: Math.min(panelLengthMm, lengthMm - targetYMm),
            width: Math.min(panelWidth, wall.width - lane * panelWidth)});
        }
      }
    }
    // Match the lane-count tolerance used when building the global lanes.
    const epsilon = 1e-9;
    const totalLaneLength = entries.reduce(function (total, wall) {
      return total + Math.ceil((wall.width - epsilon) / panelWidth) * wall.height;
    }, 0);
    const requiredReserveLength = totalLaneLength * (wasteFactor - 1);
    const packed = packPanelLanes(lanes, panelLengthMm, requiredReserveLength);
    if (!packed) return null;
    const breakdown = walls.map(function (wall) { return {name: wall.name, panels: 0}; });
    const pieces = trace ? [] : null;
    let remainingMm = 0;
    packed.bins.forEach(function (bin, panel) {
      let owner = walls.length;
      let yMm = 0;
      bin.lanes.forEach(function (lane) {
        owner = Math.min(owner, lane.wall);
        if (trace) pieces.push({wall: lane.wall, panel: panel,
          source: {x: 0, y: yMm / 1000, w: lane.width, h: lane.lengthMm / 1000},
          target: {x: lane.lane * panelWidth, y: lane.targetYMm / 1000,
            w: lane.width, h: lane.lengthMm / 1000}});
        yMm += lane.lengthMm + 3;
      });
      breakdown[owner].panels += 1;
      remainingMm += bin.remainingMm;
    });
    const best = {panels: packed.bins.length, breakdown: breakdown, pieces: pieces,
      remainingLength: remainingMm / 1000};
    // Plan the real walls first. Only surviving length offcuts count as reserve.
    const missingReserveLength = Math.max(0, requiredReserveLength - best.remainingLength);
    // Reserve panels remain whole: each supplies the full product length, with no cut.
    const reservePanels = Math.max(0,
      Math.ceil((missingReserveLength - epsilon) / panelHeight));
    const panels = best.panels + reservePanels;
    if (!Number.isFinite(requiredReserveLength) || !Number.isSafeInteger(panels)) return null;

    if (trace) best.pieces.forEach(trace);
    return {panels: panels, breakdown: best.breakdown, reservePanels: reservePanels,
      planner: packed.diagnostics, horizontalSeams: horizontalSeams,
      smallestAdditionalPieceMm: smallestAdditionalPieceMm};
  }

  function parseWholeCentimeters(input) {
    const rawValue = input.value.trim();
    if (!/^\d+$/.test(rawValue)) return null;
    const value = Number(rawValue);
    return Number.isSafeInteger(value) ? value : null;
  }

  function parseDecimal(input) {
    const rawValue = input.value.trim();

    if (rawValue === '') return null;

    const normalizedValue = rawValue.replace(',', '.');

    if (!/^(?:\d+(?:\.\d*)?|\.\d+)$/.test(normalizedValue)) {
      return null;
    }

    const value = Number(normalizedValue);

    return Number.isFinite(value) ? value : null;
  }

  function parseVariantId(value) {
    if (!/^\d+$/.test(value || '')) return null;

    const variantId = Number(value);

    return Number.isSafeInteger(variantId) && variantId > 0
      ? variantId
      : null;
  }

  function parsePriceCents(value) {
    if (typeof value === 'number') {
      return Number.isSafeInteger(value) && value >= 0
        ? value
        : null;
    }

    if (!/^\d+$/.test(value || '')) return null;

    const priceCents = Number(value);

    return Number.isSafeInteger(priceCents) && priceCents >= 0
      ? priceCents
      : null;
  }

  function getShopifyErrorDescription(responseData, status) {
    if (responseData && responseData.description) {
      if (typeof responseData.description === 'string') {
        return responseData.description;
      }

      if (typeof responseData.description === 'object') {
        const descriptions = Object.values(responseData.description)
          .flat()
          .filter(Boolean);

        if (descriptions.length) {
          return descriptions.join(' ');
        }
      }
    }

    if (
      responseData &&
      typeof responseData.message === 'string'
    ) {
      return responseData.message;
    }

    return (
      'De materialen konden niet worden toegevoegd (HTTP ' +
      status +
      ').'
    );
  }

  // Cart deltas do not prove ownership. Never remove items after an uncertain add.
  async function addPanelCartItemsSafely(options) {
    const request = options.request;
    const root = options.root;
    const fail = (message, review = false, refresh = false) =>
      ({ok: false, message: message, needsReview: review, refreshCart: refresh});
    const review = (detail = '') => fail((detail ? detail + ' ' : '') + 'De toevoeging kon niet volledig worden bevestigd. Er zijn geen artikelen automatisch verwijderd; mogelijk is een deel toegevoegd. Controleer de aantallen in je winkelwagen en vernieuw daarna deze pagina voordat je opnieuw toevoegt.', true, true);
    const wanted = new Map();
    if (!Array.isArray(options.items) || options.items.length === 0) {
      return fail('De bestelling bevat geen geldige materialen. Er is niets toegevoegd.');
    }
    for (const item of options.items) {
      if (!item || !Number.isSafeInteger(item.id) || item.id < 1 ||
          !Number.isSafeInteger(item.quantity) || item.quantity < 1 ||
          wanted.has(String(item.id))) {
        return fail('De paneel- en kitvarianten of aantallen zijn niet geldig. Er is niets toegevoegd.');
      }
      wanted.set(String(item.id), item.quantity);
    }
    function plain(item) {
      return !item.selling_plan_allocation && !item.selling_plan &&
        (!item.properties || Object.keys(item.properties).length === 0);
    }
    function identity(item) {
      const properties = Object.entries(item.properties || {}).sort((a, b) => a[0].localeCompare(b[0]));
      const plan = item.selling_plan_allocation || item.selling_plan || null;
      return JSON.stringify([String(item.variant_id), properties, plan]);
    }
    function groups(cart) {
      const values = new Map();
      for (const item of cart.items) {
        const key = identity(item);
        values.set(key, (values.get(key) || 0) + item.quantity);
      }
      return values;
    }
    function same(a, b) {
      const left = groups(a), right = groups(b);
      return [...new Set([...left.keys(), ...right.keys()])]
        .every(key => (left.get(key) || 0) === (right.get(key) || 0));
    }
    function quantity(cart, id, plainOnly = false) {
      return cart.items.reduce((total, item) => total +
        (String(item.variant_id) === String(id) && (!plainOnly || plain(item)) ? item.quantity : 0), 0);
    }
    function validate(cart) {
      if (!cart || !Array.isArray(cart.items) || cart.items.some(item =>
        !Number.isSafeInteger(item.variant_id) || item.variant_id < 1 ||
        !Number.isSafeInteger(item.quantity) || item.quantity < 0 ||
        typeof item.key !== 'string' || !item.key)) throw new Error('Invalid cart');
      if (!Number.isSafeInteger(cart.items.reduce((sum, item) => sum + item.quantity, 0))) throw new Error('Invalid cart total');
      return cart;
    }
    async function call(path, body) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), options.timeoutMs || 15000);
      try {
        const response = await request(root + path, {
          method: body ? 'POST' : 'GET', cache: 'no-store', signal: controller.signal,
          headers: {'Accept': 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest'},
          ...(body ? {body: JSON.stringify(body)} : {})
        });
        const data = await response.json();
        if (!response.ok || (data && Number(data.status) >= 400)) {
          throw new Error(options.describeError(data, response.status));
        }
        return data;
      } finally { clearTimeout(timer); }
    }
    const read = async () => validate(await call('cart.js'));
    function delta(before, after) {
      const beforeOther = {items: before.items.filter(item => !wanted.has(String(item.variant_id)) || !plain(item))};
      const afterOther = {items: after.items.filter(item => !wanted.has(String(item.variant_id)) || !plain(item))};
      if (!same(beforeOther, afterOther)) return null;
      const result = new Map();
      for (const [id, amount] of wanted) {
        const added = quantity(after, id, true) - quantity(before, id, true);
        if (added < 0 || added > amount) return null;
        result.set(id, added);
      }
      return result;
    }
    function acknowledged(receipt, after) {
      try { validate(receipt); } catch (_) { return false; }
      if (new Set(receipt.items.map(item => item.key)).size !== receipt.items.length) return false;
      if (receipt.items.length === 0 || receipt.items.some(item =>
        !wanted.has(String(item.variant_id)) || !plain(item) || item.quantity < 1 ||
        (item.properties != null && (typeof item.properties !== 'object' || Array.isArray(item.properties))) ||
        !after.items.some(current => current.key === item.key &&
          identity(current) === identity(item) && current.quantity === item.quantity))) return false;
      // add.js returns the merged line quantity, not just the requested increment.
      return [...wanted].every(([id, amount]) => {
        const received = quantity(receipt, id, true);
        return received >= amount && received <= quantity(after, id, true);
      });
    }
    let before;
    try { before = await read(); }
    catch (_) { return fail('De winkelwagen kon niet worden gecontroleerd. Er is niets toegevoegd. Probeer het opnieuw.'); }
    const already = quantity(before, options.panelId);
    if (Number.isFinite(options.maximum) && options.maximum >= 0) {
      const remaining = Math.max(0, Math.floor(options.maximum) - already);
      if (wanted.get(String(options.panelId)) > remaining) {
        return fail('Er zitten al ' + already + ' panelen van dit product in je winkelwagen. Je kunt nog maximaal ' + remaining + ' toevoegen. Pas je berekening aan. Er is niets toegevoegd.');
      }
    }
    let addError = '';
    let receipt;
    try { receipt = await call('cart/add.js', {items: options.items}); }
    catch (error) { addError = error && error.message ? error.message : 'Toevoegen is niet gelukt.'; }
    let after;
    try { after = await read(); }
    catch (_) { return review(addError); }
    const changes = delta(before, after);
    if (!addError && acknowledged(receipt, after) && changes &&
        [...wanted].every(([id, amount]) => changes.get(id) === amount)) {
      return {ok: true, needsReview: false, refreshCart: true};
    }
    // Even a zero delta cannot exclude a late server-side add after a timeout.
    // Keep partial additions and concurrent changes; never retry or roll back.
    return review(addError);
  }

  function initializeCalculator(root) {
    if (root.dataset.panelCalculatorInitialized === 'true') return;

    root.dataset.panelCalculatorInitialized = 'true';

    const sectionId = root.dataset.sectionId;
    const formId = root.dataset.formId;
    const parsedPanelDimensions = parsePanelDimensionsCm(root.dataset.productTitle);
    const panelLengthCm = parsedPanelDimensions === null
      ? DEFAULT_PANEL_LENGTH_CM
      : parsedPanelDimensions.lengthCm;
    const panelWidthCm = parsedPanelDimensions === null
      ? DEFAULT_PANEL_WIDTH_CM
      : parsedPanelDimensions.widthCm;
    const panelHeight = panelLengthCm / 100;
    const panelWidth = panelWidthCm / 100;
    root.dataset.panelLengthCm = String(panelLengthCm);
    root.dataset.panelWidthCm = String(panelWidthCm);
    root.dataset.panelLengthSource = parsedPanelDimensions === null ? 'fallback' : 'title';
    root.dataset.panelWidthSource = root.dataset.panelLengthSource;
    const panelSizeElement = root.querySelector('[data-panel-size]');
    if (panelSizeElement) {
      panelSizeElement.textContent = numberFormatter.format(panelHeight) +
        ' × ' + numberFormatter.format(panelWidth) + ' meter';
    }

    const toggleButton = root.querySelector('[data-panel-toggle]');
    const toggleText = root.querySelector('[data-panel-toggle-text]');
    const toggleIcon = root.querySelector('[data-panel-toggle-icon]');
    const contentElement = root.querySelector('[data-panel-content]');
    const wallsContainer = root.querySelector('[data-panel-walls]');
    const addWallButton = root.querySelector('[data-panel-add]');
    const calculateButton = root.querySelector('[data-panel-calculate]');
    const cartSubmitButton = root.querySelector('[data-panel-add-to-cart]');
    const withKitChoice = root.querySelector('[data-panel-choice-with-kit]');
    const panelsOnlyChoice = root.querySelector('[data-panel-choice-panels-only]');
    const deductionInput = root.querySelector('[data-panel-deduction]');
    const wasteToggle = root.querySelector('[data-panel-waste-toggle]');
    const errorElement = root.querySelector('[data-panel-error]');
    const statusElement = root.querySelector('[data-panel-status]');
    const availabilityElement = root.querySelector('[data-panel-availability]');
    const resultsElement = root.querySelector('[data-panel-results]');
    const seamsNoticeElement = root.querySelector('[data-panel-seams-notice]');
    const netAreaElement = root.querySelector('[data-panel-net-area]');
    const wasteAreaElement = root.querySelector('[data-panel-waste-area]');
    const panelCountElement = root.querySelector('[data-panel-count]');
    const kitCountElement = root.querySelector('[data-panel-kit-count]');

    const wallResultsElement = root.querySelector(
      '[data-panel-wall-results]'
    );

    const variantPricesElement = root.querySelector(
      '[data-panel-variant-prices]'
    );

    const pricesElement = root.querySelector(
      '[data-panel-prices]'
    );

    const panelPriceOption = root.querySelector(
      '[data-panel-price-panels-option]'
    );

    const panelPriceLabel = root.querySelector(
      '[data-panel-price-panels-label]'
    );

    const panelPriceAmount = root.querySelector(
      '[data-panel-price-panels]'
    );

    const combinedPriceOption = root.querySelector(
      '[data-panel-price-combined-option]'
    );

    const combinedPriceLabel = root.querySelector(
      '[data-panel-price-combined-label]'
    );

    const combinedPriceAmount = root.querySelector(
      '[data-panel-price-combined]'
    );

    const priceUnavailableElement = root.querySelector(
      '[data-panel-price-unavailable]'
    );

    const kitPriceUnavailableElement = root.querySelector(
      '[data-panel-price-kit-unavailable]'
    );

    const priceShippingElement = root.querySelector(
      '[data-panel-price-shipping]'
    );

    let recommendedPanels = 0;
    let recommendedKitTubes = 0;
    let isAddingToCart = false;
    let cartAddSucceeded = false;
    let cartFeedbackVersion = 0;
    let cartNeedsReview = false;
    let preferredChoice = 'with-kit';

    if (
      !sectionId ||
      !formId ||
      !toggleButton ||
      !toggleText ||
      !toggleIcon ||
      !contentElement ||
      !wallsContainer ||
      !addWallButton ||
      !calculateButton ||
      !cartSubmitButton ||
      !withKitChoice ||
      !panelsOnlyChoice ||
      !deductionInput ||
      !wasteToggle ||
      !errorElement ||
      !statusElement ||
      !availabilityElement ||
      !resultsElement ||
      !netAreaElement ||
      !wasteAreaElement ||
      !panelCountElement ||
      !kitCountElement ||
      !wallResultsElement ||
      !variantPricesElement ||
      !pricesElement ||
      !panelPriceOption ||
      !panelPriceLabel ||
      !panelPriceAmount ||
      !combinedPriceOption ||
      !combinedPriceLabel ||
      !combinedPriceAmount ||
      !priceUnavailableElement ||
      !kitPriceUnavailableElement ||
      !priceShippingElement
    ) {
      return;
    }

    let panelVariantPrices = {};

    try {
      const parsedVariantPrices = JSON.parse(
        variantPricesElement.textContent
      );

      if (
        parsedVariantPrices &&
        typeof parsedVariantPrices === 'object' &&
        !Array.isArray(parsedVariantPrices)
      ) {
        panelVariantPrices = parsedVariantPrices;
      }
    } catch (parseError) {
      panelVariantPrices = {};
    }

    function setExpanded(isExpanded) {
      contentElement.hidden = !isExpanded;

      toggleButton.setAttribute(
        'aria-expanded',
        String(isExpanded)
      );

      toggleText.textContent = isExpanded
        ? 'Calculator sluiten'
        : 'Bereken hoeveel panelen ik nodig heb';

      toggleIcon.textContent = isExpanded ? '−' : '+';
    }

    function clearMessages() {
      errorElement.textContent = '';
      errorElement.hidden = true;
      clearCartSuccess();
    }

    function restoreCartSubmitText() {
      if (!isAddingToCart) {
        cartSubmitButton.textContent = cartAddSucceeded
          ? 'Succesvol toegevoegd!'
          : ADD_TO_CART_TEXT;
        cartSubmitButton.classList.toggle('homewall-panel-calculator-v2__apply--success', cartAddSucceeded);
      }
    }

    function clearCartSuccess() {
      cartFeedbackVersion += 1;
      cartAddSucceeded = false;
      statusElement.textContent = '';
      statusElement.hidden = true;
      restoreCartSubmitText();
    }

    function selectPurchaseChoice(choice) {
      preferredChoice =
        choice === 'panels-only'
          ? 'panels-only'
          : 'with-kit';

      withKitChoice.checked =
        preferredChoice === 'with-kit';

      panelsOnlyChoice.checked =
        preferredChoice === 'panels-only';
    }

    function hidePriceIndications() {
      pricesElement.hidden = true;
      panelPriceOption.hidden = true;
      combinedPriceOption.hidden = true;

      priceUnavailableElement.hidden = true;
      kitPriceUnavailableElement.hidden = true;
      priceShippingElement.hidden = true;

      panelPriceLabel.textContent = '';
      panelPriceAmount.textContent = '';
      combinedPriceLabel.textContent = '';
      combinedPriceAmount.textContent = '';
    }

    function formatMoney(priceCents) {
      if (
        !Number.isSafeInteger(priceCents) ||
        priceCents < 0
      ) {
        return null;
      }

      if (
        window.Shopify &&
        typeof window.Shopify.formatMoney === 'function'
      ) {
        try {
          const formattedMoney =
            window.Shopify.formatMoney(priceCents);

          if (
            typeof formattedMoney === 'string' &&
            formattedMoney.trim() !== ''
          ) {
            return formattedMoney;
          }
        } catch (formatError) {
          // Lokale valuta-fallback hieronder.
        }
      }

      const currencyCode =
        (root.dataset.currencyCode || '').toUpperCase();

      if (!/^[A-Z]{3}$/.test(currencyCode)) {
        return null;
      }

      try {
        const locale =
          document.documentElement &&
          document.documentElement.lang
            ? document.documentElement.lang
            : undefined;

        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: currencyCode
        }).format(priceCents / 100);
      } catch (formatError) {
        return null;
      }
    }

    function updatePriceIndications() {
      hidePriceIndications();

      const hasValidResult =
        Number.isSafeInteger(recommendedPanels) &&
        recommendedPanels > 0 &&
        Number.isSafeInteger(recommendedKitTubes) &&
        recommendedKitTubes > 0;

      if (!hasValidResult) return;

      pricesElement.hidden = false;

      const panelLabel =
        recommendedPanels === 1
          ? 'paneel'
          : 'panelen';

      const kitLabel =
        recommendedKitTubes === 1
          ? 'tube'
          : 'tubes';

      panelPriceLabel.textContent =
        'Alleen ' +
        recommendedPanels +
        ' ' +
        panelLabel;

      combinedPriceLabel.textContent =
        recommendedPanels +
        ' ' +
        panelLabel +
        ' + ' +
        recommendedKitTubes +
        ' ' +
        kitLabel +
        ' montagekit';

      panelPriceOption.hidden = false;
      combinedPriceOption.hidden = false;

      const panelState = getPanelCartState();

      const selectedPanelVariantPriceCents =
        panelState.panelVariantId
          ? parsePriceCents(
              panelVariantPrices[
                String(panelState.panelVariantId)
              ]
            )
          : null;

      if (selectedPanelVariantPriceCents === null) {
        priceUnavailableElement.hidden = false;
        return;
      }

      const panelSubtotalCents =
        recommendedPanels *
        selectedPanelVariantPriceCents;

      if (!Number.isSafeInteger(panelSubtotalCents)) {
        priceUnavailableElement.hidden = false;
        return;
      }

      const formattedPanelSubtotal =
        formatMoney(panelSubtotalCents);

      if (!formattedPanelSubtotal) {
        priceUnavailableElement.hidden = false;
        return;
      }

      panelPriceAmount.innerHTML =
        formattedPanelSubtotal;

      priceShippingElement.hidden = false;

      const kitVariantId =
        parseVariantId(root.dataset.kitVariantId);

      const kitVariantPriceCents =
        parsePriceCents(root.dataset.kitPriceCents);

      const kitIsAvailable =
        root.dataset.kitAvailable === 'true' &&
        Boolean(kitVariantId);

      if (
        !kitIsAvailable ||
        kitVariantPriceCents === null
      ) {
        kitPriceUnavailableElement.hidden = false;
        return;
      }

      const kitSubtotalCents =
        recommendedKitTubes *
        kitVariantPriceCents;

      const combinedTotalCents =
        panelSubtotalCents +
        kitSubtotalCents;

      if (
        !Number.isSafeInteger(kitSubtotalCents) ||
        !Number.isSafeInteger(combinedTotalCents)
      ) {
        kitPriceUnavailableElement.hidden = false;
        return;
      }

      const formattedCombinedTotal =
        formatMoney(combinedTotalCents);

      if (!formattedCombinedTotal) {
        kitPriceUnavailableElement.hidden = false;
        return;
      }

      combinedPriceAmount.innerHTML =
        formattedCombinedTotal;
    }

    function hideResults() {
      recommendedPanels = 0;
      recommendedKitTubes = 0;

      wallResultsElement.innerHTML = '';
      wallResultsElement.hidden = true;

      clearCartSuccess();
      hidePriceIndications();

      resultsElement.hidden = true;
      if (seamsNoticeElement) {
        seamsNoticeElement.textContent = '';
        seamsNoticeElement.hidden = true;
      }

      withKitChoice.disabled = true;
      panelsOnlyChoice.disabled = true;
      cartSubmitButton.disabled = true;

      availabilityElement.textContent = '';
      availabilityElement.hidden = true;
    }

    function invalidateResult() {
      clearMessages();
      hideResults();
    }

    function showError(message, field, keepResults) {
      if (!keepResults) {
        hideResults();
      }

      statusElement.textContent = '';
      statusElement.hidden = true;

      errorElement.textContent = message;
      errorElement.hidden = false;

      if (field) {
        field.setAttribute(
          'aria-invalid',
          'true'
        );

        field.focus();
      } else {
        errorElement.focus();
      }
    }

    function clearInvalidFields() {
      root
        .querySelectorAll('[aria-invalid="true"]')
        .forEach(function (field) {
          field.removeAttribute('aria-invalid');
        });
    }

    function getPanelCartState() {
      const productForm =
        document.getElementById(formId);

      const quantityInput =
        productForm &&
        productForm.querySelector(
          'input[name="quantity"]'
        );

      const panelVariantInput =
        productForm &&
        productForm.querySelector(
          'input[name="id"]'
        );

      const submitContainer =
        productForm &&
        productForm.querySelector('.submit');

      const submitButton =
        submitContainer &&
        submitContainer.querySelector(
          'button[type="submit"]'
        );

      const panelVariantId =
        panelVariantInput
          ? parseVariantId(panelVariantInput.value)
          : null;

      const panelIsAvailable = Boolean(
        panelVariantId &&
          submitButton &&
          !submitButton.disabled &&
          !submitButton.classList.contains(
            'disabled'
          ) &&
          !submitContainer.classList.contains(
            'unavailable'
          )
      );

      return {
        productForm: productForm,
        quantityInput: quantityInput,
        panelVariantId: panelVariantId,
        available: panelIsAvailable
      };
    }

    function updateActionAvailability() {
      const hasValidResult =
        Number.isSafeInteger(recommendedPanels) &&
        recommendedPanels > 0 &&
        Number.isSafeInteger(recommendedKitTubes) &&
        recommendedKitTubes > 0;

      const panelState =
        getPanelCartState();

      const kitIsAvailable =
        root.dataset.kitAvailable === 'true' &&
        Boolean(
          parseVariantId(root.dataset.kitVariantId)
        );

      availabilityElement.textContent = '';
      availabilityElement.hidden = true;

      if (cartNeedsReview || calculatorCartState.needsReview) {
        withKitChoice.disabled = true;
        panelsOnlyChoice.disabled = true;
        cartSubmitButton.disabled = true;
        availabilityElement.textContent = 'Controleer je winkelwagen en vernieuw daarna deze pagina voordat je opnieuw toevoegt.';
        availabilityElement.hidden = false;
        return;
      }

      if (!hasValidResult) {
        withKitChoice.disabled = true;
        panelsOnlyChoice.disabled = true;
        cartSubmitButton.disabled = true;
        return;
      }

      if (!panelState.available) {
        withKitChoice.disabled = true;
        panelsOnlyChoice.disabled = true;
        cartSubmitButton.disabled = true;

        availabilityElement.textContent =
          'Dit wandpaneel is momenteel niet beschikbaar.';

        availabilityElement.hidden = false;
        return;
      }

      if (!kitIsAvailable) {
        selectPurchaseChoice('panels-only');
      } else {
        selectPurchaseChoice(preferredChoice);
      }

      withKitChoice.disabled =
        isAddingToCart || calculatorCartState.adding ||
        !kitIsAvailable;

      panelsOnlyChoice.disabled =
        isAddingToCart || calculatorCartState.adding;

      const selectedChoiceIsAvailable =
        (withKitChoice.checked &&
          !withKitChoice.disabled) ||
        (panelsOnlyChoice.checked &&
          !panelsOnlyChoice.disabled);

      cartSubmitButton.disabled =
        cartAddSucceeded || isAddingToCart || calculatorCartState.adding ||
        !selectedChoiceIsAvailable;

      if (!kitIsAvailable) {
        availabilityElement.textContent =
          'Montagekit is momenteel niet beschikbaar. Je kunt de panelen wel afzonderlijk toevoegen.';

        availabilityElement.hidden = false;
      }
    }

    function updateWallRows() {
      const walls = Array.from(
        wallsContainer.querySelectorAll(
          '[data-panel-wall]'
        )
      );

      walls.forEach(function (wall, index) {
        const wallNumber = index + 1;

        const title =
          wall.querySelector(
            '[data-panel-wall-title]'
          );

        const nameEditor =
          wall.querySelector(
            '[data-panel-wall-name-editor]'
          );

        const editButton =
          wall.querySelector(
            '[data-panel-wall-edit]'
          );

        const widthInput =
          wall.querySelector(
            '[data-panel-width]'
          );

        const heightInput =
          wall.querySelector(
            '[data-panel-height]'
          );

        const widthLabel =
          wall.querySelector(
            'label[for^="HomewallPanelCalculatorV2Width-"]'
          );

        const heightLabel =
          wall.querySelector(
            'label[for^="HomewallPanelCalculatorV2Height-"]'
          );

        const removeButton =
          wall.querySelector(
            '[data-panel-remove]'
          );

        const widthId =
          'HomewallPanelCalculatorV2Width-' +
          sectionId +
          '-' +
          wallNumber;

        const heightId =
          'HomewallPanelCalculatorV2Height-' +
          sectionId +
          '-' +
          wallNumber;

        const defaultName =
          'Wand ' + wallNumber;

        const customName =
          nameEditor &&
          nameEditor.value.trim() !== ''
            ? nameEditor.value.trim()
            : defaultName;

        title.textContent = customName;

        if (nameEditor) {
          nameEditor.placeholder =
            defaultName;

          nameEditor.setAttribute(
            'aria-label',
            'Naam van ' +
              defaultName +
              ' wijzigen'
          );
        }

        if (editButton) {
          editButton.setAttribute(
            'aria-label',
            'Naam van ' +
              customName +
              ' wijzigen'
          );

          editButton.title =
            'Naam wijzigen';
        }

        widthInput.id = widthId;
        heightInput.id = heightId;

        widthLabel.htmlFor = widthId;
        heightLabel.htmlFor = heightId;

        removeButton.hidden =
          walls.length === 1;

        removeButton.setAttribute(
          'aria-label',
          customName + ' verwijderen'
        );
      });
    }

    function addWall() {
      const firstWall =
        wallsContainer.querySelector(
          '[data-panel-wall]'
        );

      const newWall =
        firstWall.cloneNode(true);

      newWall
        .querySelectorAll('input')
        .forEach(function (input) {
          input.value = '';
          input.removeAttribute(
            'aria-invalid'
          );
        });

      const nameEditor =
        newWall.querySelector(
          '[data-panel-wall-name-editor]'
        );

      if (nameEditor) {
        nameEditor.hidden = true;
        delete nameEditor.dataset.originalValue;
      }

      newWall.classList.remove(
        'is-editing-name'
      );

      wallsContainer.appendChild(newWall);

      updateWallRows();
      invalidateResult();

      const newWidthInput =
        newWall.querySelector(
          '[data-panel-width]'
        );

      if (newWidthInput) {
        newWidthInput.focus();
      }
    }

    function removeWall(button) {
      const walls =
        wallsContainer.querySelectorAll(
          '[data-panel-wall]'
        );

      if (walls.length <= 1) {
        return;
      }

      const wall =
        button.closest(
          '[data-panel-wall]'
        );

      const nextFocusTarget =
        wall.previousElementSibling ||
        wall.nextElementSibling;

      wall.remove();

      updateWallRows();
      invalidateResult();

      if (nextFocusTarget) {
        const nextWidth =
          nextFocusTarget.querySelector(
            '[data-panel-width]'
          );

        if (nextWidth) {
          nextWidth.focus();
        }
      }
    }

    function calculatePanels() {
  clearMessages();
  clearInvalidFields();

  const walls = Array.from(
    wallsContainer.querySelectorAll('[data-panel-wall]')
  );

  let grossArea = 0;
  const wallData = [];

  for (let index = 0; index < walls.length; index += 1) {
    const wall = walls[index];

    const widthInput = wall.querySelector('[data-panel-width]');
    const heightInput = wall.querySelector('[data-panel-height]');
    const titleElement = wall.querySelector('[data-panel-wall-title]');

    const wallName =
      titleElement &&
      titleElement.textContent.trim() !== ''
        ? titleElement.textContent.trim()
        : 'Wand ' + (index + 1);

    const widthCm = parseWholeCentimeters(widthInput);
    const heightCm = parseWholeCentimeters(heightInput);
    const width = widthCm === null ? null : widthCm / 100;
    const height = heightCm === null ? null : heightCm / 100;

    if (width === null || width <= 0) {
      showError(
        'Vul voor ' +
          wallName +
          ' een breedte in hele centimeters groter dan nul in, zonder punt of komma.',
        widthInput,
        false
      );

      return;
    }

    if (height === null || height <= 0) {
      showError(
        'Vul voor ' +
          wallName +
          ' een hoogte in hele centimeters groter dan nul in, zonder punt of komma.',
        heightInput,
        false
      );

      return;
    }

   const wallArea = width * height;

grossArea += wallArea;

wallData.push({
  name: wallName,
  area: wallArea,
  width: width,
  height: height
});
  }

  if (!Number.isFinite(grossArea) || grossArea <= 0) {
    showError(
      'De totale wandoppervlakte kon niet geldig worden berekend.',
      null,
      false
    );

    return;
  }

  let deduction = 0;

  if (deductionInput.value.trim() !== '') {
    deduction = parseDecimal(deductionInput);

    if (deduction === null || deduction < 0) {
      showError(
        'Vul een geldige aftrek van nul of meer in.',
        deductionInput,
        false
      );

      return;
    }
  }

  if (deduction > grossArea) {
    showError(
      'De aftrek mag niet groter zijn dan de totale wandoppervlakte.',
      deductionInput,
      false
    );

    return;
  }

  const netArea = grossArea - deduction;

  if (netArea <= 0) {
    showError(
      'Er blijft geen te bekleden muuroppervlakte over.',
      deductionInput,
      false
    );

    return;
  }

  /*
   * Als er een aftrek is ingesteld, verdelen we die
   * evenredig over alle ingevoerde wanden.
   */
  const deductionRatio =
    grossArea > 0
      ? deduction / grossArea
      : 0;

  const wasteFactor = wasteToggle.checked ? WASTE_FACTOR : 1;
  const wallPlan = planWallsWithOffcuts(wallData, wasteFactor, null, panelHeight, panelWidth);
  if (wallPlan === null) {
    showError(
      'De indeling kon niet betrouwbaar worden berekend. Controleer de maten of neem contact op voor advies.',
      null, false
    );
    return;
  }
  root.dataset.panelPlannerStatus = wallPlan.planner.optimal ? 'optimal' : 'fallback';
  root.dataset.panelPlannerFallback = wallPlan.planner.fallbackReason || '';
  root.dataset.panelPlannerNodes = String(wallPlan.planner.searchNodes);
  const wallBreakdown = wallPlan.breakdown;
  const panelCount = wallPlan.panels;

  const areaWithWaste =
  netArea * wasteFactor;

  const kitTubes =
  Math.ceil(panelCount * 3);

  if (
    !Number.isFinite(areaWithWaste) ||
    !Number.isSafeInteger(panelCount) ||
    panelCount < 1 ||
    !Number.isSafeInteger(kitTubes) ||
    kitTubes < 1
  ) {
    showError(
      'Het aanbevolen aantal panelen kon niet geldig worden berekend.',
      null,
      false
    );

    return;
  }

  recommendedPanels = panelCount;
  recommendedKitTubes = kitTubes;

  /*
 * Resultaat per wand alleen tonen
 * wanneer er meerdere wanden zijn.
 */
wallResultsElement.innerHTML = '';

if (wallBreakdown.length > 1) {
  const displayBreakdown = wallPlan.reservePanels > 0
    ? wallBreakdown.concat([{name: 'Extra materiaalreserve', panels: wallPlan.reservePanels}])
    : wallBreakdown;
  displayBreakdown.forEach(function (wallResult) {
    const row = document.createElement('div');

    row.className =
      'homewall-panel-calculator-v2__wall-result';

    const name = document.createElement('span');
    name.textContent = wallResult.name;

    const amount = document.createElement('strong');

    amount.textContent = wallResult.panels === 0
      ? 'Uit reststukken'
      : wallResult.panels + (wallResult.panels === 1 ? ' paneel' : ' panelen');

    row.appendChild(name);
    row.appendChild(amount);

    wallResultsElement.appendChild(row);
  });

  wallResultsElement.hidden = false;
} else {
  wallResultsElement.hidden = true;
}

  netAreaElement.textContent =
    numberFormatter.format(netArea);

  wasteAreaElement.textContent =
    numberFormatter.format(areaWithWaste);

  panelCountElement.textContent =
    panelCount + (panelCount === 1 ? ' paneel' : ' panelen');

  kitCountElement.textContent =
    kitTubes +
    (kitTubes === 1
      ? ' tube'
      : ' tubes');

  if (seamsNoticeElement) {
    let notice = wallPlan.horizontalSeams > 0
      ?
        (wallPlan.smallestAdditionalPieceMm !== null && wallPlan.smallestAdditionalPieceMm <= 10
          ? 'De aanvullende strook is 1 cm of kleiner; beoordeel de praktische uitvoerbaarheid voordat je bestelt.'
          : '')
      : '';
    if (!wallPlan.planner.optimal) {
      notice += (notice ? ' ' : '') +
        'Dit is een passende materiaalberekening. Mogelijk is een efficiëntere indeling beschikbaar.';
    }
    seamsNoticeElement.textContent = notice;
    seamsNoticeElement.hidden = notice === '';
  }

  restoreCartSubmitText();

  resultsElement.hidden = false;

  updatePriceIndications();
  updateActionAvailability();
}

    function refreshThemeCart() {
      if (
        window.ajaxCart &&
        typeof window.ajaxCart.load === 'function'
      ) {
        window.ajaxCart.load(
          false,
          true,
          false,
          true
        );

        return;
      }

      window.dispatchEvent(
        new CustomEvent('cart:refresh')
      );

      const cartToggle =
        document.querySelector(
          '[data-cart-toggle], [href="#cart"], .cart-toggle, #cart-icon-bubble'
        );

      if (cartToggle) {
        cartToggle.click();
      }
    }

    async function addCalculatedItemsToCart(
      includeKit
    ) {
      if (cartAddSucceeded || isAddingToCart || cartNeedsReview ||
          calculatorCartState.adding || calculatorCartState.needsReview) return;

      clearMessages();

      if (
        !Number.isSafeInteger(
          recommendedPanels
        ) ||
        recommendedPanels < 1 ||
        !Number.isSafeInteger(
          recommendedKitTubes
        ) ||
        recommendedKitTubes < 1
      ) {
        showError(
          'Bereken eerst een geldig aanbevolen aantal panelen.',
          null,
          false
        );

        return;
      }

      const panelState =
        getPanelCartState();

      const quantityInput =
        panelState.quantityInput;

      if (
        !panelState.productForm ||
        !quantityInput
      ) {
        showError(
          'Het productformulier kon niet volledig worden gevonden.',
          null,
          true
        );

        return;
      }

      if (!panelState.panelVariantId) {
        showError(
          'De geselecteerde paneelvariant kon niet worden bepaald.',
          null,
          true
        );

        return;
      }

      if (!panelState.available) {
        updateActionAvailability();

        showError(
          'Dit wandpaneel is momenteel niet beschikbaar.',
          null,
          true
        );

        return;
      }

      let kitVariantId = null;

      if (includeKit) {
        kitVariantId =
          parseVariantId(
            root.dataset.kitVariantId
          );

        const kitIsAvailable =
          root.dataset.kitAvailable ===
          'true';

        if (
          !kitIsAvailable ||
          !kitVariantId
        ) {
          updateActionAvailability();

          showError(
            'Montagekit is momenteel niet beschikbaar. Je kunt de panelen wel afzonderlijk toevoegen.',
            null,
            true
          );

          return;
        }
      }

      const maximum =
        quantityInput.hasAttribute('max')
          ? Number(quantityInput.max)
          : null;

      if (
        maximum !== null &&
        Number.isFinite(maximum) &&
        recommendedPanels > maximum
      ) {
        showError(
          'Het aanbevolen aantal is hoger dan het maximaal beschikbare aantal van ' +
            maximum +
            '.',
          null,
          true
        );

        return;
      }

      const minimum =
        quantityInput.hasAttribute('min')
          ? Number(quantityInput.min)
          : 1;

      if (
        Number.isFinite(minimum) &&
        recommendedPanels < minimum
      ) {
        showError(
          'Het aanbevolen aantal is lager dan het minimaal bestelbare aantal van ' +
            minimum +
            '.',
          null,
          true
        );

        return;
      }

      const step =
        quantityInput.hasAttribute('step')
          ? Number(quantityInput.step)
          : 1;

      if (
        Number.isFinite(step) &&
        step > 0 &&
        Number.isFinite(minimum)
      ) {
        const stepDifference =
          (recommendedPanels - minimum) /
          step;

        if (
          Math.abs(
            stepDifference -
              Math.round(stepDifference)
          ) > 1e-9
        ) {
          showError(
            'Het aanbevolen aantal past niet binnen de ingestelde besteleenheden van dit product.',
            null,
            true
          );

          return;
        }
      }

      const routeRoot =
        window.Shopify &&
        window.Shopify.routes &&
        window.Shopify.routes.root;

      if (
        typeof routeRoot !== 'string'
      ) {
        showError(
          'De winkelwagenroute kon niet worden bepaald.',
          null,
          true
        );

        return;
      }

      const submittedPanels =
        recommendedPanels;

      const submittedKitTubes =
        recommendedKitTubes;

      const items = [
        {
          id: panelState.panelVariantId,
          quantity: submittedPanels
        }
      ];

      if (includeKit) {
        items.push({
          id: kitVariantId,
          quantity: submittedKitTubes
        });
      }

      const submittedFeedbackVersion = cartFeedbackVersion;
      isAddingToCart = true;
      calculatorCartState.adding = true;
      window.dispatchEvent(new CustomEvent('homewall:cart-state'));

      updateActionAvailability();

      cartSubmitButton.textContent =
        includeKit
          ? 'Panelen en montagekit toevoegen…'
          : 'Panelen toevoegen…';

      try {
        const outcome = await addPanelCartItemsSafely({
          root: routeRoot,
          items: items,
          panelId: panelState.panelVariantId,
          maximum: maximum,
          request: window.fetch.bind(window),
          describeError: getShopifyErrorDescription
        });
        if (!outcome.ok) {
          cartNeedsReview = outcome.needsReview;
          calculatorCartState.needsReview = outcome.needsReview;
          showError(outcome.message, null, true);
          if (outcome.refreshCart) {
            try { refreshThemeCart(); } catch (_) { /* Preserve the specific error. */ }
          }
          return;
        }

        cartAddSucceeded = submittedFeedbackVersion === cartFeedbackVersion;
        statusElement.textContent = 'Succesvol toegevoegd.';
        statusElement.hidden = false;

        try { refreshThemeCart(); } catch (_) { /* The confirmed add remains successful. */ }
      } catch (error) {
        cartNeedsReview = true;
        calculatorCartState.needsReview = true;
        showError(
          'De winkelwagen kon niet betrouwbaar worden gecontroleerd. Controleer de aantallen en vernieuw daarna deze pagina voordat je opnieuw toevoegt.',
          null, true
        );
        try { refreshThemeCart(); } catch (_) { /* Keep the review message visible. */ }
      } finally {
        isAddingToCart = false;
        calculatorCartState.adding = false;
        window.dispatchEvent(new CustomEvent('homewall:cart-state'));

        restoreCartSubmitText();
        updatePriceIndications();
        updateActionAvailability();
      }
    }

    window.addEventListener('homewall:cart-state', updateActionAvailability);

    toggleButton.addEventListener(
      'click',
      function () {
        setExpanded(
          toggleButton.getAttribute(
            'aria-expanded'
          ) !== 'true'
        );
      }
    );

    addWallButton.addEventListener(
      'click',
      addWall
    );

    calculateButton.addEventListener(
      'click',
      calculatePanels
    );

    cartSubmitButton.addEventListener(
      'click',
      function () {
        addCalculatedItemsToCart(
          withKitChoice.checked
        );
      }
    );

    pricesElement.addEventListener(
      'change',
      function (event) {
        if (
          !isAddingToCart &&
          event.target.matches(
            '[data-panel-choice-with-kit], [data-panel-choice-panels-only]'
          ) &&
          event.target.checked
        ) {
          clearCartSuccess();
          preferredChoice =
            event.target.value;

          updateActionAvailability();
        }
      }
    );

    function handleVariantUpdate() {
      if (cartAddSucceeded || isAddingToCart) clearCartSuccess();
      updateActionAvailability();
      updatePriceIndications();
    }

    const productForm =
      document.getElementById(formId);

    if (productForm) {
      productForm.addEventListener(
        'change',
        function () {
          window.setTimeout(
            handleVariantUpdate,
            0
          );
        }
      );
    }

    window.addEventListener(
      'themeVariantUpdated',
      handleVariantUpdate
    );

    function handleSectionUnload(event) {
      if (
        event.target &&
        typeof event.target.contains ===
          'function' &&
        event.target.contains(root)
      ) {
        window.removeEventListener(
          'themeVariantUpdated',
          handleVariantUpdate
        );
        window.removeEventListener('homewall:cart-state', updateActionAvailability);

        document.removeEventListener(
          'shopify:section:unload',
          handleSectionUnload
        );
      }
    }

    document.addEventListener(
      'shopify:section:unload',
      handleSectionUnload
    );

    /*
     * Wand verwijderen of wandnaam bewerken.
     */
    wallsContainer.addEventListener(
      'click',
      function (event) {
        const removeButton =
          event.target.closest(
            '[data-panel-remove]'
          );

        if (
          removeButton &&
          wallsContainer.contains(
            removeButton
          )
        ) {
          removeWall(removeButton);
          return;
        }

        const editButton =
          event.target.closest(
            '[data-panel-wall-edit]'
          );

        if (
          editButton &&
          wallsContainer.contains(
            editButton
          )
        ) {
          const wall =
            editButton.closest(
              '[data-panel-wall]'
            );

          const editor =
            wall &&
            wall.querySelector(
              '[data-panel-wall-name-editor]'
            );

          if (!editor) return;

          editor.dataset.originalValue =
            editor.value;

          editor.hidden = false;

          wall.classList.add(
            'is-editing-name'
          );

          window.requestAnimationFrame(
            function () {
              editor.focus();
              editor.select();
            }
          );
        }
      }
    );

    /*
     * Enter = naam opslaan.
     * Escape = wijziging annuleren.
     */
    wallsContainer.addEventListener(
      'keydown',
      function (event) {
        if (
          !event.target.matches(
            '[data-panel-wall-name-editor]'
          )
        ) {
          return;
        }

        const editor = event.target;

        const wall =
          editor.closest(
            '[data-panel-wall]'
          );

        if (event.key === 'Enter') {
          event.preventDefault();

          wall.classList.remove(
            'is-editing-name'
          );

          editor.hidden = true;

updateWallRows();
invalidateResult();

return;
        }

        if (event.key === 'Escape') {
          event.preventDefault();

          editor.value =
            editor.dataset.originalValue ||
            '';

          wall.classList.remove(
            'is-editing-name'
          );

          editor.hidden = true;

          updateWallRows();
        }
      }
    );

    /*
     * Buiten het naamveld klikken = naam opslaan.
     */
    wallsContainer.addEventListener(
      'focusout',
      function (event) {
        if (
          !event.target.matches(
            '[data-panel-wall-name-editor]'
          )
        ) {
          return;
        }

        const editor = event.target;

        const wall =
          editor.closest(
            '[data-panel-wall]'
          );

        if (!wall) return;

        wall.classList.remove(
          'is-editing-name'
        );

        editor.hidden = true;

updateWallRows();
invalidateResult();
      }
    );

    root.addEventListener('beforeinput', function (event) {
      if (event.target.matches('[data-panel-width], [data-panel-height]') &&
          event.inputType === 'insertText' && event.data && /[^0-9]/.test(event.data)) {
        event.preventDefault();
        showError(
          'Gebruik alleen hele centimeters zonder punt of komma, bijvoorbeeld 240.',
          event.target, false
        );
      }
    });

    root.addEventListener(
  'input',
  function (event) {
    if (
      event.target.matches(
        '[data-panel-width], [data-panel-height]'
      )
    ) {
      const value = event.target.value.trim();
      event.target.removeAttribute('aria-invalid');
      invalidateResult();
      if (value !== '' && !/^\d+$/.test(value)) {
        showError(
          'Gebruik alleen hele centimeters zonder punt of komma, bijvoorbeeld 240.',
          event.target, false
        );
      }
      return;
    }

    if (event.target.matches('[data-panel-deduction]')) {
      event.target.removeAttribute('aria-invalid');
      invalidateResult();
    }
  }
);

    wasteToggle.addEventListener('change', function () {
  invalidateResult();
});

    setExpanded(false);
    updateWallRows();
    hideResults();
  }

  function initializeAll(scope) {
    const root =
      scope || document;

    if (
      root.matches &&
      root.matches(
        '[data-homewall-panel-calculator-v2]'
      )
    ) {
      initializeCalculator(root);
    }

    root
      .querySelectorAll(
        '[data-homewall-panel-calculator-v2]'
      )
      .forEach(initializeCalculator);
  }

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        initializeAll(document);
      }
    );
  } else {
    initializeAll(document);
  }

  document.addEventListener(
    'shopify:section:load',
    function (event) {
      initializeAll(event.target);
    }
  );
})();
